import { useState, useEffect } from "react";
import { 
  Box, Typography, useTheme, Button, 
  Dialog, DialogTitle, DialogContent, 
  DialogActions, Select, MenuItem, 
  FormControl, InputLabel 
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import { API_BASE_URL } from "../../config/api";

const formatDisplayDate = (value) => {
  if (!value) return "N/A";
  try {
    const date = new Date(value);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    const dateString = String(value).split("T")[0];
    return dateString || value;
  }
};

const Team = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Status update dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectionModel, setSelectionModel] = useState([]);

  // Fetch bookings data from API
  const fetchBookings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await fetch(`${API_BASE_URL}/admin/booking/all`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error("Failed to fetch bookings");
      }

      const data = await response.json();
      if (data.subCode === 200) {
        const mappedBookings = data.items.map(booking => ({
          ...booking,
          id: booking._id
        }));
        setBookings(mappedBookings);
      } else {
        setError("Failed to load bookings: " + (data.message || "Unknown error"));
      }
    } catch (err) {
      setError(err.message);
      console.error("Booking fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  // Handle button actions
  const handleButtonAction = async (bookingId, actionType) => {
    try {
      const token = localStorage.getItem("authToken");
      const endpoint = `${API_BASE_URL}/admin/booking/${actionType}/${bookingId}`;
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        // body:{"status":"active"}
      });
      const result = await response.json();
      if (result.subCode!== 200) {
        throw new Error(`Failed to ${actionType} booking`);
      }
      

      // Reload bookings after successful action
      fetchBookings();
      alert(`Booking ${actionType} successful!`);
      
    } catch (err) {
      console.error(`${actionType} booking error:`, err);
      alert(`Error: ${err.message}`);
    }
  };

  // Open status update dialog
  const handleOpenDialog = (booking) => {
    setSelectedBooking(booking);
    setSelectedStatus(booking.status);
    setOpenDialog(true);
  };

  // Close dialog without saving
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedBooking(null);
    setSelectedStatus("");
  };

  const handleExportSelectedBookings = () => {
    if (!selectionModel.length) {
      alert("Select at least one booking to export.");
      return;
    }
    const idSet = new Set(selectionModel);
    const rows = bookings.filter((b) => idSet.has(b.id));
    const exportData = rows.map((row) => ({
      "Create Date": formatDisplayDate(row.createdAt),
      Service: row.service ?? "",
      Airport: row.airport_name ?? "",
      "Booking ID": row._id ?? row.id ?? "",
      Agent: "APWU",
      Email: row.email ?? "",
      "Departure Date": formatDisplayDate(row.date_start),
      "Return Date": formatDisplayDate(row.date_end),
      "Car Reg": row.licensePlate ?? "",
      "Full Name": `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim(),
      Vehicle: [row.vehicleColor, row.vehicleMake, row.vehicleModel]
        .filter(Boolean)
        .join(" "),
      Promo: row.discountCode ?? "",
      "Contact Number": row.phone ?? "",
      "Total Price": row.total_amount ?? "",
      "Amount Paid": `£${parseFloat(row.price || 0).toFixed(2)}`,
      Status: row.status ?? "",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Parking Bookings");
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `parking-bookings-${stamp}.xlsx`);
  };

  const updateBookingStatus = async () => {
    if (!selectedBooking || !selectedStatus) return;
    
    try {
      const token = localStorage.getItem("authToken");
      const endpoint = `${API_BASE_URL}/admin/booking/edit/${selectedBooking.id}`;
      
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: selectedStatus })
      });

      if (!response.ok) {
        throw new Error("Failed to update booking status");
      }

      await response.json();
      
      // Refresh bookings
      await fetchBookings();
      handleCloseDialog();
      alert("Booking status updated successfully!");
      
    } catch (err) {
      console.error("Status update error:", err);
      alert(`Error: ${err.message}`);
    }
  };

  // Define columns for the DataGrid
  const columns = [
    {
      field: "createdAt",
      headerName: "Create Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDisplayDate(params.value),
    },
    {
      field: "service",
      headerName: "Service",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {params.row.service}
        </Box>
      ),
    },
    {
      field: "airport_name",
      headerName: "Airport",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {params.row.airport_name}
        </Box>
      ),
    },
    { 
      field: "_id", 
      headerName: "ID", 
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ 
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          lineHeight: '1.5'
        }}>
          {params.value}
        </Box>
      )
    },
    {
      field: "agent",
      headerName: "Agent",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box>
          {/* {params.row.airport_name} */}
          APWU
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 350,
      renderCell: (params) => (
        <Box sx={{ 
          whiteSpace: 'normal',
          wordBreak: 'break-all'
        }}>
          {params.value}
        </Box>
      )
    },
    {
      field: "date_start",
      headerName: "Departure Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDisplayDate(params.value),
    },
    {
      field: "date_end",
      headerName: "Return Date",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => formatDisplayDate(params.value),
    },
    {
      field: "licensePlate",
      headerName: "Car Reg",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          {params.row.licensePlate}
        </Box>
      ),
    },
    {
      field: "firstName",
      headerName: "Full Name",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          {params.row.firstName} {params.row.lastName}
        </Box>
      ),
    },
    {
      field: "vehicleMake",
      headerName: "Car Make",
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Box>
          {params.row.vehicleColor} {params.row.vehicleMake} {params.row.vehicleModel}
        </Box>
      ),
    },
    {
      field: "discountCode",
      headerName: "Promo",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          {params.row.discountCode}
        </Box>
      ),
    },
    
    
    
    
    {
      field: "phone",
      headerName: "Contact Number",
      flex: 1,
      minWidth: 130,
    },
    
    {
      field: "total_amount",
      headerName: "Total Price",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box>
          {params.row.total_amount}
        </Box>
      ),
    },
    {
      field: "price",
      headerName: "Amount Paid",
      flex: 1,
      minWidth: 150,
      renderCell: ({ value }) => `£${parseFloat(value || 0).toFixed(2)}`
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box display="flex" flexDirection="column" gap="8px">
          <Button 
            variant="contained" 
            color="secondary"
            disabled={params.row.status === "pending"}
            onClick={() => handleOpenDialog(params.row)}
            fullWidth
            size="small"
          >
            Edit Booking Status
          </Button>
          {/* <Button 
            variant="contained" 
            color="error"
            disabled={params.row.status === "ongoing"}
            onClick={() => handleButtonAction(params.row.id, "cancel/tidy")}
            fullWidth
            size="small"
          >
            Delete Booking
          </Button> */}
        </Box>
      ),
    },
    // {
    //   field: "ParkWithUSBook",
    //   headerName: "APWU Booking",
    //   flex: 1,
    //   minWidth: 150,
    //   renderCell: (params) => (
    //     <Button 
    //       variant="contained" 
    //       color="secondary"
    //       onClick={() => handleButtonAction(params.row.id, "apwuEmail")}
    //       size="small"
    //       fullWidth
    //     >
    //       Add Booking
    //     </Button>
    //   ),
    // },
  ];

  // Status update dialog component
  const StatusUpdateDialog = () => (
    <Dialog open={openDialog} onClose={handleCloseDialog}>
      <DialogTitle>Update Booking Status</DialogTitle>
      <DialogContent>
        {selectedBooking && (
          <Box mt={2} minWidth={300}>
            <Typography variant="h6" mb={2}>
              Booking ID: {selectedBooking.id}
            </Typography>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Select Status</InputLabel>
              <Select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                label="Select Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} color="secondary">
          Cancel
        </Button>
        <Button 
          onClick={updateBookingStatus} 
          color="primary"
          variant="contained"
        >
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Handle loading and error states
  if (loading) return <Box>Loading bookings...</Box>;
  if (error) return <Box>Error: {error}</Box>;

  return (
    <Box m="20px">
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="flex-end"
        justifyContent="space-between"
        gap={2}
      >
        <Header title="PARKING BOOKINGS" subtitle="Managing Bookings" />
        <Button
          variant="contained"
          color="secondary"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleExportSelectedBookings}
          disabled={selectionModel.length === 0}
          sx={{ mb: "30px" }}
        >
          Export bookings
        </Button>
      </Box>
      <StatusUpdateDialog />
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
            py: "8px",
            fontSize: "1rem",
            lineHeight: 1.5,
            color: colors.grey[100],
          },
          "& .MuiDataGrid-row": {
            minHeight: "90px !important",
            maxHeight: "none !important",
            height: "auto !important",
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? `${colors.primary[500]} !important`
                : `rgba(0, 0, 0, 0.04) !important`
            }
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
            fontSize: "1rem",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "1rem",
            },
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        <DataGrid 
          checkboxSelection 
          rows={bookings} 
          columns={columns} 
          autoHeight={false}
          disableRowSelectionOnClick
          disableColumnResize={false}
          selectionModel={selectionModel}
          onSelectionModelChange={(newModel) => setSelectionModel(newModel)}
          initialState={{
            sorting: {
              sortModel: [{ field: "createdAt", sort: "desc" }],
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Team;